import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import { SignatureBlock } from "@/components/staff/SignatureBlock";

const blockedReason = (sig, purpose) => {
  if (!sig.active) return "Inactive";
  if (purpose === "doctor_report" && sig.signature_type === "team") return "Doctor reports need a named dietitian";
  if (purpose === "doctor_report" && sig.missing_for_doctor?.length) return `Missing ${sig.missing_for_doctor.join(", ")}`;
  return null;
};

/** Ask the user to pick a signature, preview it, then run onConfirm(signature_id). */
export const SignaturePicker = ({ request, token, onClose }) => {
  const [sigs, setSigs] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!request) return;
    setChosen(null); setSigs(null);
    fetch(`${API}/staff/signatures?include_inactive=false`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setSigs(d.signatures || [])).catch(() => setSigs([]));
  }, [request, token]);

  const confirm = async () => {
    setBusy(true);
    try { await request.onConfirm(chosen.signature_id); onClose(); } finally { setBusy(false); }
  };
  const doctor = request?.purpose === "doctor_report";

  return (
    <Dialog open={!!request} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="signature-picker">
        <DialogHeader>
          <DialogTitle>Choose a signature</DialogTitle>
          <DialogDescription>{doctor ? "Reports for a doctor must be signed by a named dietitian." : "This signature will appear on the document and in the email."}</DialogDescription>
        </DialogHeader>
        {!sigs ? <Loader2 className="w-6 h-6 animate-spin text-[#FF7A00] mx-auto my-6" /> : (
          <div className="grid sm:grid-cols-2 gap-3 max-h-[45vh] overflow-y-auto pr-1">
            {sigs.map(sig => {
              const reason = blockedReason(sig, request?.purpose);
              if (doctor && sig.signature_type === "team") return null;
              const sel = chosen?.signature_id === sig.signature_id;
              return (
                <button key={sig.signature_id} disabled={!!reason} onClick={() => setChosen(sig)} data-testid={`pick-signature-${sig.signature_id}`}
                  className={`text-left rounded-xl border p-3 transition-colors relative ${reason ? "opacity-50 cursor-not-allowed bg-stone-50" : sel ? "border-[#FF7A00] bg-[#FFF7F0]" : "border-stone-200 hover:border-[#FF7A00]/50"}`}>
                  {sel && <CheckCircle2 className="w-4 h-4 text-[#FF7A00] absolute top-2 right-2" />}
                  <SignatureBlock sig={sig} token={token} compact />
                  {reason && <p className="text-[11px] text-red-600 mt-2 flex items-center gap-1" data-testid={`signature-blocked-${sig.signature_id}`}><Lock className="w-3 h-3" />{reason}</p>}
                </button>
              );
            })}
            {doctor && !sigs.some(s => !blockedReason(s, "doctor_report")) && (
              <p className="text-sm text-stone-600 sm:col-span-2" data-testid="no-doctor-signature">No named dietitian signature is ready yet. Add one with a full name and professional title in Settings → Signatures.</p>
            )}
          </div>
        )}
        {chosen && (
          <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="signature-preview">
            <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wider mb-2">How it will appear on the {request?.docLabel || "document"}</p>
            <p className="text-xs text-stone-600 mb-1">Kind regards,</p>
            <SignatureBlock sig={chosen} token={token} />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={!chosen || busy} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="signature-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{request?.actionLabel || "Continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
